/**
 * S7 — Éditeur de composants pour ouvrages composés
 * Permet d'ajouter/modifier/supprimer/réordonner les composants d'un ouvrage
 *
 * - Save onBlur sur quantity/cost inputs (pattern Atlassian inline-edit)
 * - Drag-and-drop via @dnd-kit/sortable (même pattern que SectionList)
 * - i18n via products.composite.* / products.fields.*
 */

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, Trash2, Layers } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { productsAPI } from '@/utils/productsAPI';
import { ProductSelector } from './ProductSelector';
import type { Product, ProductComponent } from '@/types/models';

interface CompositeEditorProps {
  productId: string;
  onPriceUpdate?: (sellingPriceHT: number) => void;
}

interface CompositeData {
  product: Product;
  components: (ProductComponent & { ChildProduct: Product })[];
  compositePrice?: {
    debourseSecHT: number;
    sellingPriceHT: number;
    marginAmount: number;
  };
}

// ============================================================
// Sous-composant : Input numérique avec save onBlur
// Pattern Atlassian inline-edit : état local pendant la frappe,
// un seul appel API quand l'utilisateur quitte le champ.
// Pas de setTimeout arbitraire, pas de race condition.
// ============================================================
interface BlurSaveNumberInputProps {
  value: number;
  onSave: (val: number | null) => void;
  step?: string;
  min?: string;
  className?: string;
}

const BlurSaveNumberInput: React.FC<BlurSaveNumberInputProps> = ({
  value,
  onSave,
  step = '0.01',
  min = '0',
  className,
}) => {
  const [localValue, setLocalValue] = useState(String(value));
  const committedRef = useRef(value);

  // Sync si la valeur prop change (après rechargement API)
  useEffect(() => {
    setLocalValue(String(value));
    committedRef.current = value;
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  };

  const handleBlur = () => {
    const parsed = localValue ? parseFloat(localValue) : null;
    // Ne sauvegarder que si la valeur a réellement changé
    if (parsed !== committedRef.current) {
      onSave(parsed);
    }
  };

  // Enter = confirmer et blur
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      // Escape = annuler, restaurer la valeur précédente
      setLocalValue(String(committedRef.current));
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <input
      type="number"
      step={step}
      min={min}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={className}
    />
  );
};

// ============================================================
// Sous-composant : Ligne de composant sortable
// ============================================================
interface SortableComponentRowProps {
  comp: ProductComponent & { ChildProduct: Product };
  onUpdateQuantity: (componentId: string, quantity: number) => void;
  onUpdateCostOverride: (componentId: string, value: number | null) => void;
  onRemove: (componentId: string) => void;
  formatCurrency: (n: number) => string;
}

const SortableComponentRow: React.FC<SortableComponentRowProps> = ({
  comp,
  onUpdateQuantity,
  onUpdateCostOverride,
  onRemove,
  formatCurrency,
}) => {
  const { t } = useTranslation('products');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: comp.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  const child = comp.ChildProduct;
  const effectiveCost = comp.costPriceOverride ?? child.costPrice ?? child.unitPrice;
  const lineTotal = Number(comp.quantity) * Number(effectiveCost);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`grid grid-cols-12 gap-2 items-center px-3 py-3 bg-white border rounded-xl transition-colors ${
        isDragging
          ? 'border-teal-300 shadow-lg bg-teal-50/50'
          : 'border-gray-200 hover:border-teal-300'
      }`}
    >
      {/* Drag handle */}
      <div className="col-span-1 flex justify-center">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </div>

      {/* Product name */}
      <div className="col-span-3">
        <p className="text-sm font-medium text-gray-900 truncate">
          {child.name}
        </p>
        {child.isComposite && (
          <span className="inline-flex items-center gap-1 text-xs text-teal-600">
            <Layers className="w-3 h-3" />
            {t('products.composite.badge')}
          </span>
        )}
      </div>

      {/* Quantity — save onBlur */}
      <div className="col-span-2">
        <BlurSaveNumberInput
          value={Number(comp.quantity)}
          step="0.01"
          min="0.01"
          onSave={(val) => {
            if (val !== null && val > 0) onUpdateQuantity(comp.id, val);
          }}
          className="w-full px-2 py-1.5 text-sm text-right border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
        />
      </div>

      {/* Cost override — save onBlur */}
      <div className="col-span-2">
        <BlurSaveNumberInput
          value={comp.costPriceOverride != null ? Number(comp.costPriceOverride) : Number(child.costPrice ?? child.unitPrice)}
          step="0.01"
          min="0"
          onSave={(val) => onUpdateCostOverride(comp.id, val)}
          className="w-full px-2 py-1.5 text-sm text-right border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
        />
      </div>

      {/* Line total */}
      <div className="col-span-2 text-right">
        <span className="text-sm font-medium text-gray-900">
          {formatCurrency(lineTotal)}
        </span>
      </div>

      {/* Actions */}
      <div className="col-span-2 flex justify-end">
        <button
          type="button"
          onClick={() => onRemove(comp.id)}
          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          title={t('common.actions.delete', { ns: 'common' })}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================================
// Composant principal
// ============================================================
export const CompositeEditor: React.FC<CompositeEditorProps> = ({
  productId,
  onPriceUpdate,
}) => {
  const { t } = useTranslation('products');
  const [data, setData] = useState<CompositeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSelector, setShowSelector] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref stable pour éviter infinite loop (onPriceUpdate inline non mémorisé par le parent)
  const onPriceUpdateRef = useRef(onPriceUpdate);
  onPriceUpdateRef.current = onPriceUpdate;

  // DnD sensors (même pattern que SectionList)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const loadComponents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await productsAPI.getProductWithComponents(productId);
      setData(result);
      if (result.compositePrice && onPriceUpdateRef.current) {
        onPriceUpdateRef.current(result.compositePrice.sellingPriceHT);
      }
    } catch (err: any) {
      setError(err.message || t('products.loadError'));
    } finally {
      setLoading(false);
    }
  }, [productId, t]);

  useEffect(() => {
    loadComponents();
  }, [loadComponents]);

  const handleAddComponent = async (product: Product) => {
    try {
      setError(null);
      await productsAPI.addComponent(productId, {
        childProductId: product.id,
        quantity: 1,
        unit: product.unit || undefined,
      });
      await loadComponents();
    } catch (err: any) {
      setError(err.message || t('products.saveError'));
    }
  };

  const handleUpdateQuantity = async (componentId: string, quantity: number) => {
    if (quantity <= 0) return;
    try {
      setError(null);
      await productsAPI.updateComponent(productId, componentId, { quantity });
      await loadComponents();
    } catch (err: any) {
      setError(err.message || t('products.saveError'));
    }
  };

  const handleUpdateCostOverride = async (componentId: string, value: number | null) => {
    try {
      setError(null);
      await productsAPI.updateComponent(productId, componentId, {
        costPriceOverride: value,
      });
      await loadComponents();
    } catch (err: any) {
      setError(err.message || t('products.saveError'));
    }
  };

  const handleRemoveComponent = async (componentId: string) => {
    try {
      setError(null);
      await productsAPI.removeComponent(productId, componentId);
      await loadComponents();
    } catch (err: any) {
      setError(err.message || t('products.deleteError'));
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !data) return;

    const oldIndex = data.components.findIndex(c => c.id === active.id);
    const newIndex = data.components.findIndex(c => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // Optimistic reorder in UI
    const reordered = arrayMove(data.components, oldIndex, newIndex);
    setData(prev => prev ? { ...prev, components: reordered } : prev);

    // Persist to backend
    try {
      setError(null);
      await productsAPI.reorderComponents(
        productId,
        reordered.map(c => c.id)
      );
    } catch (err: any) {
      setError(err.message || t('products.saveError'));
      // Rollback
      await loadComponents();
    }
  };

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
      </div>
    );
  }

  const components = data?.components || [];
  const compositePrice = data?.compositePrice;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-teal-600" />
          <h3 className="text-base font-semibold text-gray-900">
            {t('products.composite.components')}
          </h3>
          <span className="text-sm text-gray-500">
({components.length})
          </span>
        </div>
        <button
          type="button"
          onClick={() => setShowSelector(true)}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('products.composite.addComponent')}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {components.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <Layers className="w-10 h-10 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">{t('products.composite.noComponents')}</p>
          <p className="text-sm text-gray-400 mt-1">
            {t('products.composite.noComponentsDesc')}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Header */}
          <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-medium text-gray-500 uppercase">
            <div className="col-span-1" />
            <div className="col-span-3">{t('products.fields.product')}</div>
            <div className="col-span-2 text-right">{t('products.stock.quantity')}</div>
            <div className="col-span-2 text-right">{t('products.composite.costPrice')}</div>
            <div className="col-span-2 text-right">{t('common.labels.total', { ns: 'common' })}</div>
            <div className="col-span-2" />
          </div>

          {/* Sortable rows */}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={components.map(c => c.id)} strategy={verticalListSortingStrategy}>
              {components.map((comp) => (
                <SortableComponentRow
                  key={comp.id}
                  comp={comp}
                  onUpdateQuantity={handleUpdateQuantity}
                  onUpdateCostOverride={handleUpdateCostOverride}
                  onRemove={handleRemoveComponent}
                  formatCurrency={formatCurrency}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Totaux */}
      {compositePrice && components.length > 0 && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{t('products.composite.debourseSecHT')}</span>
            <span className="font-medium text-gray-900">
              {formatCurrency(compositePrice.debourseSecHT)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{t('products.composite.margin')}</span>
            <span className="font-medium text-teal-600">
              + {formatCurrency(compositePrice.marginAmount)}
            </span>
          </div>
          <div className="flex justify-between text-base font-semibold border-t border-teal-300 pt-2">
            <span className="text-gray-900">{t('products.composite.sellingPriceHT')}</span>
            <span className="text-teal-700">
              {formatCurrency(compositePrice.sellingPriceHT)}
            </span>
          </div>
        </div>
      )}

      {/* Product Selector Modal */}
      {showSelector && (
        <ProductSelector
          onSelectProduct={handleAddComponent}
          onClose={() => setShowSelector(false)}
        />
      )}
    </div>
  );
};
